// ── Branch editor ─────────────────────────────────────────────────────────
//
// v1 scope: name + a SIMPLE expression per branch. Editing a branch's inner
// task list happens on the canvas itself (drag/drop into the branch's
// subgraph), not here — see graph.ts's module comment on why branch content
// round-trips via `__task` rather than being reconstructed from the graph.
import { Plus, Trash2 } from 'lucide-react'
import type { Branch, BranchTask, SimpleExpr } from '../../types'
import { Field, SelectInput, TextInput } from './fields'

export interface BranchDrawerProps {
  task: BranchTask
  onChange: (updater: (t: BranchTask) => BranchTask) => void
}

const OPERATORS: SimpleExpr['operator'][] = ['EQ', 'NEQ', 'GT', 'LT', 'GTE', 'LTE']

function asSimpleExpr(expression: Branch['expression']): SimpleExpr {
  if (expression && expression.type === 'SIMPLE') return expression
  return { type: 'SIMPLE', sourcePath: '', operator: 'EQ', value: '' }
}

export function BranchDrawer({ task, onChange }: BranchDrawerProps) {
  const branches = task.configuration.branches

  function updateBranch(index: number, patch: Partial<Branch>) {
    onChange((t) => ({
      ...t,
      configuration: {
        ...t.configuration,
        branches: t.configuration.branches.map((b, i) => (i === index ? { ...b, ...patch } : b)),
      },
    }))
  }

  function updateExpr(index: number, patch: Partial<SimpleExpr>) {
    updateBranch(index, { expression: { ...asSimpleExpr(branches[index].expression), ...patch } })
  }

  function removeBranch(index: number) {
    onChange((t) => ({
      ...t,
      configuration: { ...t.configuration, branches: t.configuration.branches.filter((_, i) => i !== index) },
    }))
  }

  function addBranch() {
    onChange((t) => ({
      ...t,
      configuration: { ...t.configuration, branches: [...t.configuration.branches, { name: 'New Branch', tasks: [] }] },
    }))
  }

  return (
    <div className="flex flex-col gap-4">
      {branches.map((branch, i) => {
        const expr = asSimpleExpr(branch.expression)
        return (
          <div key={i} className="rounded-md border border-[#1e2c44] bg-[#0b1220] p-3 flex flex-col gap-2.5">
            <div className="flex items-center gap-2">
              <TextInput
                value={branch.name}
                onChange={(e) => updateBranch(i, { name: e.target.value })}
                className="flex-1"
              />
              <button
                onClick={() => removeBranch(i)}
                className="p-1 rounded text-[#5b6b85] hover:text-red-400 hover:bg-[#1a2740] shrink-0"
                title="Remove branch"
              >
                <Trash2 size={12} />
              </button>
            </div>

            <Field label="Source path">
              <TextInput
                value={expr.sourcePath}
                onChange={(e) => updateExpr(i, { sourcePath: e.target.value })}
                placeholder="tasks.step_kyc.stepResult.result"
              />
            </Field>

            <div className="grid grid-cols-2 gap-2">
              <Field label="Operator">
                <SelectInput
                  value={expr.operator}
                  onChange={(e) => updateExpr(i, { operator: e.target.value as SimpleExpr['operator'] })}
                >
                  {OPERATORS.map((op) => (
                    <option key={op} value={op}>
                      {op}
                    </option>
                  ))}
                </SelectInput>
              </Field>
              <Field label="Value">
                <TextInput value={expr.value} onChange={(e) => updateExpr(i, { value: e.target.value })} />
              </Field>
            </div>
          </div>
        )
      })}

      <button
        onClick={addBranch}
        className="flex items-center gap-1.5 self-start px-2.5 py-1.5 rounded-md text-xs font-medium text-[#94a3b8] border border-[#1e2c44] hover:bg-[#1a2740] transition-colors"
      >
        <Plus size={12} /> Add branch
      </button>
    </div>
  )
}
